import React from 'react';
import { useCookies } from 'react-cookie';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { guestTenant, updateGuestTenant } from 'services/tenant-service';
import * as tenantService from 'services/tenant-service';
import jwt from 'jwt-decode';

interface ITenantContext {
    currentTenant: guestTenant | null;
    hiddenTenant: guestTenant | null;
    setCurrentTenant: (tenant: guestTenant, options?: { update: boolean }) => void;
    updateTenant: (input: updateGuestTenant) => Promise<boolean>;
    setTenantByZoneNum: (zoneNum: string) => void;
}

export const TenantContext = React.createContext<ITenantContext>({
    currentTenant: null,
    hiddenTenant: null,
    setCurrentTenant: (tenant: guestTenant) => null,
    updateTenant: (input: updateGuestTenant) => new Promise<boolean>((resolve, reject) => { return false }),
    setTenantByZoneNum: (zoneNum: string) => null
});

export const TenantProvider = ({ children }: any) => {
    const [currentTenant, _setCurrentTenant] = React.useState<guestTenant | null>(null);
    const [hiddenTenant, setHiddenTenant] = React.useState<guestTenant | null>(null);
    const [zoneId, setZoneId] = React.useState<string | null>(null);
    const [update, setUpdate] = React.useState<boolean>(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const [cookies, setCookie, removeCookie] = useCookies();
    const navigate = useNavigate();

    React.useEffect(() => {
        if (!update) return;
        const cookie = cookies['__tenant'];
        const zoneName = searchParams.get('zoneId');
        const token = localStorage.getItem('token');

        if (token === null && zoneName !== null ) {
            // Do the code below, don't use the cookie
            if (cookie) {
                removeCookie('__tenant', { path: '/' });
            }
        }
        else if (cookie) {
            // First check if token is present, if not, use the cookie
            if (token !== null) {
                var user = JSON.stringify(jwt(token))
                var tenantId = JSON.parse(user).tenantid;
                tenantService.get(tenantId)
                .then((res: guestTenant) => {
                    setUpdate(false);
                    removeCookie('__tenant', { path: '/' });
                    setCurrentTenant(res);
                    setZoneId(res.zoneNum);
                    navigate({
                        pathname: window.location.pathname,
                        search: `?zoneId=${res.zoneNum}`,
                    }, { replace: true });
                })
                .catch((error: Error) => {
                    searchParams.delete('zoneId');
                    setSearchParams(searchParams);
                });
            }
            else {
                tenantService.get(cookies['__tenant'])
                .then((res: guestTenant) => {
                    _setCurrentTenant(res);
                })
                .catch((error: Error) => {
                    console.log(error);
                });
            }
            return;
        }
        
        if (zoneName === null && zoneId !== null) {
            searchParams.set('zoneId', zoneId);
            setSearchParams(searchParams);
        } else if (zoneName !== null && zoneId === null) {
            if (currentTenant !== null && currentTenant.zoneNum === zoneName) {
                searchParams.delete('zoneId');
                setSearchParams(searchParams);
                return;
            }
            tenantService.getByZone(zoneName)
                .then((res: guestTenant) => {
                    setCurrentTenant(res);
                    setZoneId(zoneName);
                })
                .catch((error: Error) => {
                    searchParams.delete('zoneId');
                    setSearchParams(searchParams);
                });
        } else if (zoneName === null && zoneId === null) {
            setTenantByZoneNum('5345')
        }
    }, [cookies, searchParams]);

    const setTenantByZoneNum = (zoneNum: string): void => {
      tenantService.getByZone(zoneNum)
        .then((res: guestTenant) => {
          setCurrentTenant(res);
          setZoneId(zoneNum);
          searchParams.set('zoneId', zoneNum);
          setSearchParams(searchParams);
        })
        .catch(err => {
          console.log(err)
        })
    }

    const setCurrentTenant = (tenant: guestTenant, options?: { update: boolean }) => {
        setCookie('__tenant', tenant.id, { path: '/', sameSite: 'none', secure: true })
        window.sessionStorage.setItem('__tenantId', tenant.id);
        if (options && options.update === false) {
            setUpdate(false);
            setHiddenTenant(tenant);
            return;
        }
        _setCurrentTenant(tenant);
    }

    const updateTenant = async (input: updateGuestTenant): Promise<boolean> => {
        if (currentTenant) {
            return tenantService.updateCurrentTenant(currentTenant.id, input)
                .then((res: guestTenant) => {
                    setCurrentTenant(res, { update: true });
                    return true;
                })
                .catch((error: Error) => {
                    return false;
                });
        }
        return new Promise<boolean>((resolve, reject) => { return false });
    }

    const value: ITenantContext = {
        currentTenant,
        hiddenTenant,
        setCurrentTenant,
        updateTenant,
        setTenantByZoneNum,
    };

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};

export const useTenantContext = () => React.useContext(TenantContext);